import { useCallback } from "react";

import {
  PHASE2_SAVE_BANK_DETAILS,
  PHASE2_UPLOAD_DOCUMENT,
  phase2SubmitUrl,
} from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import {
  postEmptyBody,
  postFormDataMultipart,
} from "../../../../utils/api.ts";
import type { BankStepValues } from "../schemas/onboard-post-join.schema.ts";
import type { Phase2DocType } from "../doc-types.ts";
import { useSqeOnboardingApi } from "../../onboarding/sqe/sqe-onboarding-link-context.tsx";
import { SQE_PHASE2 } from "../../onboarding/sqe/sqe-onboarding-api.ts";

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export function useOnboardPostJoinPhase2() {
  const isSqe = useSqeOnboardingApi();
  const saveBankDetails = useCallback(
    async (
      employeeId: number,
      values: BankStepValues,
      cancelledCheque: File,
      signal?: AbortSignal,
    ) => {
      const fd = new FormData();
      fd.append("employeeId", String(employeeId));
      fd.append("beneficiaryName", values.beneficiaryName);
      fd.append("accountNumber", values.accountNumber);
      fd.append("bankName", values.bankName);
      fd.append("branchName", values.branchName);
      if (!isSqe) {
        fd.append("bankZipCode", values.bankZipCode);
        fd.append("bankPostcode", values.bankPostcode);
      }
      fd.append("ifscCode", values.ifscCode);
      fd.append("cancelledCheque", cancelledCheque);
      await postFormDataMultipart(isSqe ? `${SQE_PHASE2}/save-bank-details` : PHASE2_SAVE_BANK_DETAILS, isSqe ? undefined : accessToken(), fd, signal);
    },
    [isSqe],
  );

  const uploadDocument = useCallback(
    async (
      employeeId: number,
      docType: Phase2DocType,
      file: File,
      signal?: AbortSignal,
    ) => {
      const fd = new FormData();
      fd.append("employeeId", String(employeeId));
      fd.append("docType", docType);
      fd.append("file", file);
      await postFormDataMultipart(isSqe ? `${SQE_PHASE2}/upload-document` : PHASE2_UPLOAD_DOCUMENT, isSqe ? undefined : accessToken(), fd, signal);
    },
    [isSqe],
  );

  const submitPhase2 = useCallback(async (employeeId: number, signal?: AbortSignal) => {
    await postEmptyBody(isSqe ? `${SQE_PHASE2}/submit?employeeId=${encodeURIComponent(String(employeeId))}` : phase2SubmitUrl(employeeId), isSqe ? undefined : accessToken(), signal);
  }, [isSqe]);

  return {
    saveBankDetails,
    uploadDocument,
    submitPhase2,
  };
}
